import { Injectable, inject } from '@angular/core';
import { EnumTranslator, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { CompanyParametersQuery, VatRateForSaleQuery, VatReleaseReasonItem, VatReleaseReasonQuery, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectBase, IdentifiableValueConverter, LocalContext, ModelBase, ObservableCollection, PropertyChangedJoinPoint, RepositoryBase } from '@skylab-shared/core';
import { DocumentItemModel, EnumTranslatorBase, LabeledEnumValue, LabeledEnumValueConverter, VatReleaseReasonSource } from '@skylab-shared/dataModel';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormBase, FormBaseViewModel } from '../form/form-base';

export type DocumentFormViewModelVatReleaseReasonExtension = {
    vatReleaseReasonValueConverter: IdentifiableValueConverter;
    vatReleaseReasons: VatReleaseReasonItem[];
    vatReleaseReason: {
        value: number | null | undefined;
    };
    vatReleaseReasonSourceValueConverter: LabeledEnumValueConverter<VatReleaseReasonSource>;
    vatReleaseReasonsSource: LabeledEnumValue[];
    vatReleaseReasonSource: {
        value: VatReleaseReasonSource | null | undefined;
    };
    isVatReleaseReasonCustomNameShown(): boolean;
    isVatReleaseReasonVisible$: Observable<boolean>;
};

interface DocumentModel extends ModelBase {
    vatReleaseReasonId?: number | null;
    vatReleaseReasonCustomName?: string | null;
    vatReleaseReasonSource?: VatReleaseReasonSource | null;
    issuedByVatPayer: boolean;
    items: ObservableCollection<DocumentItemModel>;
}

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelVatReleaseReasonExtensionService {
    private enumTranslator = inject(EnumTranslator) as EnumTranslatorBase;
    private weaver = inject(Weaver) as WeaverBase;

    public extendViewModel<TViewModelPart, TViewModel extends TViewModelPart & FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): TViewModelPart & DocumentFormViewModelVatReleaseReasonExtension {
        return Object.assign(viewModel as any, this.getViewModelExtension(formComponent, localContext));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): DocumentFormViewModelVatReleaseReasonExtension {
        const model = formComponent.bo.model;
        const companyParameters = localContext.getPreloaded(CompanyParametersQuery);
        const isVatReleaseReasonVisible$ = new BehaviorSubject(this.isVatReleaseReasonVisible(model, localContext));

        const handleVatReleaseChange = () => {
            isVatReleaseReasonVisible$.next(this.isVatReleaseReasonVisible(model, localContext));
            if (!isVatReleaseReasonVisible$.value) {
                model.vatReleaseReasonId = model.vatReleaseReasonCustomName = model.vatReleaseReasonSource = null;
            } else {
                model.vatReleaseReasonId = companyParameters.vatReleaseReasonId;
                model.vatReleaseReasonCustomName = companyParameters.vatReleaseReasonCustomName;
                model.vatReleaseReasonSource = companyParameters.vatReleaseReasonSource;
            }
        };

        formComponent.registerListening(() => {
            isVatReleaseReasonVisible$.unsubscribe();
        });

        formComponent.registerListening(model.items.onCollectionChanged(_ => {
            handleVatReleaseChange();
        }));

        formComponent.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(formComponent.bo)(DocumentItemModel, ['vatRateId'])(
            _ => {
                handleVatReleaseChange();
            }
        )));

        formComponent.registerListening(formComponent.bo.model.onPropertyChanged(ctx => {
            if (ctx.propertyName === 'issuedByVatPayer') {
                handleVatReleaseChange();
            }
        }));

        const vatReleaseReasons = localContext.getPreloaded(VatReleaseReasonQuery) || [];
        const vatReleaseReasonValueConverter = new IdentifiableValueConverter(vatReleaseReasons);

        const vatReleaseReasonsSource = this.enumTranslator.getLabeledEnumValueArray(VatReleaseReasonSource);
        const vatReleaseReasonSourceValueConverter = new LabeledEnumValueConverter<VatReleaseReasonSource>(this.enumTranslator, VatReleaseReasonSource);

        const viewModelExtension: DocumentFormViewModelVatReleaseReasonExtension = {
            vatReleaseReasons,
            vatReleaseReasonValueConverter,
            vatReleaseReasonsSource,
            vatReleaseReasonSourceValueConverter,
            vatReleaseReason: {
                get value() {
                    return model.vatReleaseReasonId;
                },
                set value(value) {
                    model.vatReleaseReasonId = value;
                    model.vatReleaseReasonCustomName = null;
                    model.vatReleaseReasonSource = value ? VatReleaseReasonSource.Act : null;
                }
            },
            vatReleaseReasonSource: {
                get value() {
                    return model.vatReleaseReasonSource;
                },
                set value(value) {
                    model.vatReleaseReasonSource = value;
                }
            },
            isVatReleaseReasonCustomNameShown: (): boolean => {
                const releaseReason = vatReleaseReasons.find(v => v.id === model.vatReleaseReasonId);
                return !!(releaseReason && releaseReason.customReleaseName);
            },
            isVatReleaseReasonVisible$: isVatReleaseReasonVisible$.asObservable()
        };

        return viewModelExtension;
    }

    private isVatReleaseReasonVisible (model: DocumentModel, localContext: LocalContext): boolean {
        if (!model.issuedByVatPayer) return true;

        const exemptVatRate = localContext.firstPreloaded(VatRateForSaleQuery, {exempt: true})?.id;
        return model.items.some(elem => exemptVatRate === elem.vatRateId);
    }
}
